import { uiSurface } from "shared/ui/uiSurface"
import {
  ExpandMore,
  GroupOutlined,
  TimerOutlined,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material"
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  Stack,
  styled,
  Typography,
} from "@mui/material"
import dayjs from "dayjs"
import { MuiColorInput } from "mui-color-input"
import { FC, memo, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { HaulerEmployee } from "src/db/HaulerEmployee"
import { MainOrderFilterSection } from "src/features/order/components"
import {
  SkeletonWrapper,
  TooltipWrapper,
} from "src/features/shared-module/components"
import {
  appConstants,
  defaultEmployeeOrders,
} from "src/features/shared-module/constants"
import { getFullName } from "src/features/shared-module/functions"
import { RouteSummaryRaw } from "src/features/shared-module/models"
import { useAppDispatch, useAppSelector } from "../hooks"
import {
  isLoadingSelector,
  isOrdersLoadingSelector,
  setEmployeeColor,
  setVisibilityInMap,
} from "../slices/dashboard.slice"
import { EmployeeOrderList } from "./EmployeeOrderList"

const StyledAccordian = styled(Accordion)(({ theme }) => ({
  boxShadow: "none",
  borderRadius: "12px !important",
  "&::before": {
    height: 0,
  },
  "& .MuiAccordionSummary-root": {
    padding: 0,
    borderRadius: 12,
    ".MuiAccordionSummary-expandIconWrapper": {
      position: "absolute",
      right: "15px",
      color: theme.palette.primary.main,
      transition: "transform 0.3s ease",
    },
  },
  "& .MuiAccordionSummary-content": {
    margin: 0,
    ".MuiBox-root": {
      width: "100%",
    },
  },
}))

export const HaulerEmployeeList: FC = memo(() => {
  const { t } = useTranslation()
  const scrollingOrder = useAppSelector(
    (state) => state.dashboard.scrollingDetail,
  )

  const [expanded, setExpanded] = useState<Record<number, boolean>>({})

  const isLoading = useAppSelector(isLoadingSelector)
  const isOrdersLoading = useAppSelector(isOrdersLoadingSelector)

  const haulerEmployeeList = useAppSelector(
    (state) => state.dashboard.employeeList,
  )

  useEffect(() => {
    if (
      !scrollingOrder ||
      !scrollingOrder?.haulerEmployeeId ||
      expanded[scrollingOrder.haulerEmployeeId]
    )
      return
    setExpandValue(scrollingOrder.haulerEmployeeId, true)
  }, [scrollingOrder, expanded])

  const setExpandValue = (haulerEmployeeId: number, expandValue: boolean) => {
    setExpanded((prev) => {
      return {
        ...prev,
        [haulerEmployeeId]: expandValue,
      }
    })
  }

  if (isLoading) {
    return (
      <Stack spacing={1}>
        {Array.from({ length: 10 }, (e, i) => i).map((e: number) => {
          return (
            <SkeletonWrapper
              key={e}
              height={110}
              sxProps={{
                borderRadius: 2,
              }}
            />
          )
        })}
      </Stack>
    )
  }
  return (
    <Stack
      {...uiSurface("UI-DSP-029")}
      data-testid="employee-list"
      spacing={1}
      overflow={"auto"}
      height={"calc(100% - 40px)"}
    >
      <Box
        sx={(theme) => ({
          position: "sticky",
          top: 0,
          zIndex: 2,
          backgroundColor: theme.palette.common.white,
        })}
      >
        <MainOrderFilterSection />
      </Box>
      {isOrdersLoading && (
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ py: 1 }}>
          <CircularProgress size={16} />
          <Typography variant="caption" color="text.secondary">
            {t("loadingOrders")}
          </Typography>
        </Stack>
      )}
      <Stack spacing={1} paddingX={0.5}>
        {haulerEmployeeList.length === 0 ? (
          <Stack
            alignItems="center"
            justifyContent="center"
            spacing={1.5}
            sx={(theme) => ({
              py: 6,
              px: 3,
              borderRadius: 3,
              border: `1px dashed ${theme.palette.divider}`,
              backgroundColor: theme.palette.action.hover,
            })}
          >
            <GroupOutlined
              sx={(theme) => ({
                fontSize: 40,
                color: theme.palette.text.disabled,
              })}
            />
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, color: "text.secondary" }}
            >
              {t("noDrivers")}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: "text.disabled", textAlign: "center" }}
            >
              {t("noDriversHint")}
            </Typography>
          </Stack>
        ) : (
          haulerEmployeeList.map((haulerEmployee) => {
            return (
              <StyledAccordian
                {...uiSurface("UI-DSP-031")}
                data-testid={`employee-accordion-${haulerEmployee.id}`}
                sx={{
                  background: "transparent",
                }}
                expanded={expanded[haulerEmployee.id] || false}
                onChange={() => {
                  setExpandValue(
                    haulerEmployee.id,
                    !expanded[haulerEmployee.id],
                  )
                }}
                key={haulerEmployee.id}
                disableGutters
                slotProps={{ transition: { unmountOnExit: true } }}
              >
                <AccordionSummary expandIcon={<ExpandMore />} component={"div"}>
                  <HaulerEmployeeDetail haulerEmployeeId={haulerEmployee.id} />
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    padding: 0.5,
                  }}
                >
                  <EmployeeOrderList haulerEmployeeId={haulerEmployee.id} />
                </AccordionDetails>
              </StyledAccordian>
            )
          })
        )}
      </Stack>
    </Stack>
  )
})

const HaulerEmployeeDetail: FC<{
  haulerEmployeeId: number
}> = memo(({ haulerEmployeeId }) => {
  const { t } = useTranslation()
  const employeeAllOrderDetail = useAppSelector((state) => {
    return (
      state.dashboard.employeeOrders?.[haulerEmployeeId] ||
      defaultEmployeeOrders
    )
  })

  const isDataVisibleInMap = useAppSelector((state) => {
    return state.dashboard.employeeDetailVisibility[haulerEmployeeId]
  })

  const isEmployeeSecExpanded = useAppSelector((state) => {
    return state.dashboard.expandableSections.employee
  })

  const {
    grouped: groupedOrdersMap,
    individual: orderListWithoutRoute,
    onTimeOrders,
    delayedOrders,
  } = employeeAllOrderDetail

  const maxEndTime = useAppSelector((state) => {
    let endTimeList: number[] = []
    const routeSummaryIds = Object.keys(groupedOrdersMap).map((e) => Number(e))
    routeSummaryIds.forEach((routeSummaryId) => {
      const routeSummaryRaw = state.dashboard.orderRouteSummaryMap?.[
        routeSummaryId
      ]?.routeDetail?.raw as RouteSummaryRaw
      const eTime = routeSummaryRaw?.routes?.features?.[0]?.attributes?.EndTime
      if (eTime) {
        endTimeList.push(eTime)
      }
    })
    return endTimeList.length
      ? dayjs(Math.max(...endTimeList)).format(appConstants.hourTimeFormat)
      : null
  })

  let assignedRouteOrdersCount = 0
  Object.keys(groupedOrdersMap).forEach((key) => {
    assignedRouteOrdersCount += groupedOrdersMap[Number(key)].length
  })

  const employeeOrderCount =
    orderListWithoutRoute.length + assignedRouteOrdersCount

  const haulerEmployee = useAppSelector((state) =>
    state.dashboard.employeeList.find((e) => e.id === haulerEmployeeId),
  ) as HaulerEmployee

  const dispatch = useAppDispatch()

  const onChangeColor = (color: string) => {
    dispatch(
      setEmployeeColor({
        id: haulerEmployeeId,
        color: color,
      }),
    )
  }

  const onChangeVisibility = (visibility: boolean) => {
    dispatch(
      setVisibilityInMap({
        haulerEmployeeId: haulerEmployeeId,
        visibility: visibility,
      }),
    )
  }

  return (
    <Paper
      data-testid={`employee-detail-${haulerEmployeeId}`}
      variant="outlined"
      sx={(theme) => ({
        width: "100%",
        display: "flex",
        borderRadius: 3,
        padding: 1.5,
        height: 110,
        backgroundColor: theme.palette.background.paper,
        borderColor: theme.palette.divider,
        transition: "all 0.2s ease",
        ":hover": {
          backgroundColor: theme.palette.primary.light,
          borderColor: theme.palette.primary.main,
          boxShadow: `0 2px 12px ${theme.palette.primary.main}12`,
        },
      })}
    >
      <Stack width={isEmployeeSecExpanded ? 380 : 240}>
        <Stack
          direction={"row"}
          spacing={1}
          alignItems={"center"}
          width={"100%"}
        >
          <Box
            sx={{
              height: "20px",
              width: "20px !important",
            }}
            onClick={(event) => {
              event.stopPropagation()
              event.preventDefault()
            }}
          >
            <MuiColorInput
              data-testid={`employee-color-${haulerEmployeeId}`}
              isAlphaHidden={true}
              variant="standard"
              format="hex"
              value={haulerEmployee.color}
              onChange={onChangeColor}
              sx={{
                width: 20,
                "& .MuiInput-root": {
                  "&::before": {
                    content: "none",
                  },
                  "& .notranslate": {
                    display: "none",
                  },
                  "& .MuiInputBase-input": { display: "none" },
                  "& .MuiInputAdornment-root": { margin: 0 },
                  "& .MuiButton-root": {
                    width: 20,
                    height: 20,
                    minWidth: 20,
                    padding: 0,
                    borderRadius: "50%",
                    "& .MuiBox-root": {
                      width: 20,
                      height: 20,
                      borderRadius: "50%",
                    },
                  },
                },
              }}
            />
          </Box>
          <Stack direction={"row"} alignItems={"center"} spacing={1}>
            {isDataVisibleInMap &&
            Object.values(isDataVisibleInMap).some(
              (value: any) => value === false,
            ) ? (
              <IconButton
                data-testid={`employee-visibility-${haulerEmployeeId}`}
                onClick={(event) => {
                  event.stopPropagation()
                  onChangeVisibility(true)
                }}
              >
                <VisibilityOff />
              </IconButton>
            ) : (
              <IconButton
                data-testid={`employee-visibility-${haulerEmployeeId}`}
                onClick={(event) => {
                  event.stopPropagation()
                  onChangeVisibility(false)
                }}
              >
                <Visibility />
              </IconButton>
            )}

            <TooltipWrapper
              placement="top"
              title={getFullName(
                haulerEmployee.firstName,
                haulerEmployee.lastName,
              )}
            >
              <Typography
                data-testid={`employee-name-${haulerEmployeeId}`}
                variant="subtitle2"
                fontWeight={600}
                noWrap={true}
                maxWidth={isEmployeeSecExpanded ? 310 : 170}
              >
                {getFullName(haulerEmployee.firstName, haulerEmployee.lastName)}
              </Typography>
            </TooltipWrapper>
          </Stack>
        </Stack>
        <Stack
          marginY={"auto"}
          direction={"row"}
          alignItems={"center"}
          borderRadius={1}
          spacing={1}
        >
          <Typography
            data-testid={`employee-order-count-${haulerEmployeeId}`}
            variant="subtitle1"
            color="primary"
          >
            {employeeOrderCount}
          </Typography>
          <OrderStatusView
            onTimeOrders={onTimeOrders}
            delayedOrders={delayedOrders}
          />
        </Stack>
      </Stack>
      {assignedRouteOrdersCount > 0 && (
        <>
          <Divider
            sx={(theme) => ({
              mx: 2,
              borderRightWidth: 2,
              borderColor: theme.palette.secondary.main,
            })}
            flexItem
            orientation="vertical"
          />
          <Stack spacing={1}>
            {maxEndTime && (
              <Typography variant="subtitle2" fontSize={12} fontWeight={600}>
                {t("end")} {maxEndTime}
              </Typography>
            )}
            <Stack direction={"row"} spacing={1} alignItems={"center"}>
              <Box
                sx={(theme) => ({
                  backgroundColor: theme.palette.secondary.light,
                  borderRadius: 2,
                  width: 160,
                  padding: 0.5,
                })}
              >
                <Stack
                  sx={{
                    p: 1,
                  }}
                  direction={"row"}
                  justifyContent={"space-between"}
                >
                  <Typography color="secondary" variant="h6">
                    {t("deliveredLabel", {
                      completed: 0,
                      total: employeeOrderCount,
                    })}
                  </Typography>
                </Stack>
                <Stack
                  direction={"row"}
                  spacing={0.4}
                  sx={(theme) => ({
                    backgroundColor: theme.palette.secondary.light,
                  })}
                >
                  {[
                    {
                      color: "primary",
                      value: assignedRouteOrdersCount,
                    },
                    {
                      color: "success",
                      value: 0,
                    },
                    {
                      color: "error",
                      value: 0,
                    },
                  ].map((e, index) => {
                    return (
                      <Typography
                        sx={(theme) => ({
                          backgroundColor: theme.palette.background.default,
                        })}
                        variant="body2"
                        flexGrow={1}
                        textAlign={"center"}
                        color={e.color}
                        key={index}
                        borderRadius={1}
                      >
                        {e.value}
                      </Typography>
                    )
                  })}
                </Stack>
              </Box>
            </Stack>
          </Stack>
        </>
      )}
    </Paper>
  )
})

export const OrderStatusView: FC<{
  onTimeOrders: number
  delayedOrders: number
}> = ({ onTimeOrders, delayedOrders }) => {
  return (
    <Stack direction={"row"} alignItems={"center"} spacing={0.5}>
      <TimerOutlined
        sx={(theme) => ({
          fontSize: 16,
          color: theme.palette.text.secondary,
        })}
      />
      <Stack direction={"row"} borderRadius={99} overflow="hidden" width={72}>
        <Typography
          color="common.white"
          sx={{
            backgroundColor: "#16a34a",
            width: "50%",
            paddingY: 0.25,
            fontSize: "0.7rem",
            fontWeight: 700,
            fontVariantNumeric: "tabular-nums",
          }}
          textAlign={"center"}
          variant="subtitle2"
        >
          {onTimeOrders}
        </Typography>
        <Typography
          color="common.white"
          sx={{
            backgroundColor: "#dc2626",
            width: "50%",
            paddingY: 0.25,
            fontSize: "0.7rem",
            fontWeight: 700,
            fontVariantNumeric: "tabular-nums",
          }}
          textAlign={"center"}
          variant="subtitle2"
        >
          {delayedOrders}
        </Typography>
      </Stack>
    </Stack>
  )
}
